import { Box, CircularProgress, Typography } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useMSSP from "../../../../api/hooks/useMSSP";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { QueryParam } from "../../../../utils/constants";
import { useLocalTime } from "../../../../utils/timestamp";
import getTableColumns from "./columns";
import { formatFieldHeaderName } from "./fieldUtils";
import TableSkeletonRow from "./TableSkeletonRow";

const DefaultLoadingOverlay = () => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      background: "rgba(255, 255, 255, 0.95)",
      overflow: "hidden",
    }}
  >
    <Box sx={{ width: "100%" }}>
      {Array.from({ length: 24 }, (_, i) => i).map((i) => (
        <TableSkeletonRow key={i} index={i} />
      ))}
    </Box>
  </Box>
);

export default function RdsEntitiesTable({
  entityType,
  rows,
  assets,
  visibleColumns,
  sortModel,
  onSortModelChange,
  onLoadMore,
  hasMore,
  isLoading,
  isFetching,
  isLoadingMore,
  totalCount,
  setCheckedEntityIDs,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  sx,
}) {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedTenantIds, hasMultipleSelectedTenants } = useMSSP();

  const onCellClick = (params) => {
    if (params.field === "__check__") {
      trackEvent("user toggled an entity row checkbox in the entities table");
      return;
    }
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set(
      QueryParam.entity,
      encodeURIComponent(params.row.unique_id)
    );
    setSearchParams(updatedSearchParams);
  };

  const showLocalTime = useLocalTime();
  const baseColumns = getTableColumns(
    entityType,
    rows,
    assets,
    colors,
    showLocalTime,
    selectedTenantIds,
    hasMultipleSelectedTenants
  );

  const customColumns =
    visibleColumns && visibleColumns.length > 0
      ? visibleColumns.map((fieldName) => ({
          field: fieldName,
          headerName: formatFieldHeaderName(fieldName),
          minWidth: 150,
          flex: 0.5,
          sortable: true,
          resizable: true,
          renderCell: (params) => {
            const value = params.value;

            if (value === null || value === undefined) {
              return "-";
            }

            if (Array.isArray(value)) {
              return value.length > 0 ? value.join(", ") : "-";
            }

            if (typeof value === "object") {
              try {
                return JSON.stringify(value);
              } catch {
                return String(value);
              }
            }

            return String(value);
          },
        }))
      : [];

  const columns = [...baseColumns, ...customColumns];

  const handleRowsScrollEnd = () => {
    if (onLoadMore && hasMore && !isLoadingMore) {
      onLoadMore();
    }
  };

  const shouldAutoHeight =
    !hasMore && !isLoading && (totalCount ?? rows.length) <= 12;

  return (
    <Box position="relative">
      {(totalCount || totalCount === 0) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            height: "16px",
            padding: "0px 8px",
            visibility: isFetching && rows.length > 0 ? "visible" : "hidden",
          }}
        >
          <CircularProgress size={14} />
          <Typography variant="caption" fontSize="14px" color="text.secondary">
            Refreshing data
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          borderRadius: 3,
          border: `1px solid #d9d9d9`,
          overflow: "hidden",
          height: shouldAutoHeight ? "auto" : "calc(100vh - 280px)",
          minHeight: shouldAutoHeight ? undefined : 500,
          ...sx,
        }}
      >
        <StyledDataGrid
          enablePagination={false}
          autoHeight={shouldAutoHeight}
          boxSXProps={{
            border: "none",
            borderRadius: 0,
            ...(shouldAutoHeight ? {} : { height: "100%" }),
          }}
          rows={rows}
          columns={columns}
          loading={isLoading}
          sortingMode="server"
          sortModel={sortModel || []}
          onSortModelChange={onSortModelChange}
          onCellClick={onCellClick}
          checkboxSelection
          onRowSelectionModelChange={(checkedEntityIDs) => {
            setCheckedEntityIDs(checkedEntityIDs);
          }}
          getRowId={(row) => row.entity_id + "-" + row.customer_id}
          onRowsScrollEnd={handleRowsScrollEnd}
          scrollEndThreshold={200}
          hideFooter
          columnVisibilityModel={
            columnVisibilityModel || { local_user_admin: false }
          }
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          sx={shouldAutoHeight ? undefined : { height: "100%" }}
          slots={{
            loadingOverlay: DefaultLoadingOverlay,
          }}
        />
      </Box>

      {isLoadingMore && (
        <Box
          sx={{
            position: "absolute",
            bottom: 8,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 1.5,
              px: 2,
              background: colors.common_white,
              borderRadius: 2,
              border: `1px solid ${colors.grey_24percent}`,
              boxShadow: "0 -4px 10px rgba(17, 24, 39, 0.08)",
            }}
          >
            <CircularProgress size={18} thickness={4} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 13, fontWeight: 500 }}
            >
              Loading more rows...
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
